/**
 * Breadcrumb Navigation Component
 * 
 * Displays a navigation trail showing user's location within the app.
 * Dashboard is always the root when user is logged in.
 * 
 * Usage:
 *   <Breadcrumb items={[
 *     { label: 'Courses', href: '/catalog' },
 *     { label: 'Introduction to Compliance' }
 *   ]} />
 * 
 * Or use auto-detection:
 *   <Breadcrumb />
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Route to breadcrumb label mapping
const ROUTE_LABELS = {
  // Learner routes
  'dashboard': 'Dashboard',
  'catalog': 'Course Catalog',
  'courses': 'Courses',
  'course': 'Course',
  'learning-labs': 'Learning Labs',
  'learning-lab': 'Learning Lab',
  'learning-pathways': 'Learning Pathways',
  'learning-pathway': 'Learning Pathway',
  'masterclasses': 'Masterclasses',
  'masterclass': 'Masterclass',
  'campaigns': 'Campaigns',
  'campaign': 'Campaign',
  'achievements': 'Achievements',
  'certificates': 'Certificates',
  'profile': 'Profile',
  'settings': 'Settings',
  'wsp': 'WSP Analytics',
  'regulatory-alerts': 'Regulatory Alerts',
  'navigate-academy': 'Navigate Academy',
  'academy': 'Academy',
  'external-learning': 'External Learning',
  'ebooks': 'eBooks',
  'ebook': 'eBook',
  
  // Admin routes
  'admin': 'Admin',
  'tenant-setup': 'Tenant Setup',
  'course-builder': 'Course Builder',
  'ai-course': 'AI Course Creator',
  'create': 'Create',
  'edit': 'Edit',
  'media-library': 'Media Library',
  'assessments': 'Assessments',
  'navi-assessments': 'NAVI Assessments',
  'users': 'Users',
  'analytics': 'Analytics',
  'reports': 'Reports',
  'exports': 'Exports',
  'gamification': 'Gamification',
  'enterprise-tenants': 'Enterprise Tenants',
  'feature-flags': 'Feature Flags',
  
  // Auth routes (hidden from breadcrumb)
  'login': null,
  'register': null,
  'forgot-password': null,
  'enterprise-sso': null,
  'sso': null,
};

// Routes that should show admin breadcrumb path
const ADMIN_ROUTES = [
  'admin', 'tenant-setup', 'course-builder', 'ai-course', 
  'media-library', 'assessments', 'navi-assessments', 'enterprise-tenants',
  'feature-flags', 'gamification-admin'
];

/**
 * Generate breadcrumb items from current path
 */
const generateBreadcrumbs = (pathname, isAdmin = false) => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];
  
  // Always start with Dashboard for logged-in users
  breadcrumbs.push({
    label: 'Dashboard',
    href: '/dashboard',
    isRoot: true
  });
  
  // Check if this is an admin route
  const isAdminRoute = segments.some(seg => ADMIN_ROUTES.includes(seg)) || segments[0] === 'admin';
  
  if (isAdminRoute && segments[0] !== 'admin') {
    // Add Admin as second level for admin routes not starting with /admin
    breadcrumbs.push({
      label: 'Admin',
      href: '/admin'
    });
  }
  
  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Skip if it's a UUID or ID-like segment (show parent label instead)
    const isId = /^[a-f0-9-]{8,}$|^(course|lab|pathway|user|org|tenant)_[a-z0-9]+$/i.test(segment);
    
    // Get label from mapping or capitalize segment
    let label = ROUTE_LABELS[segment];
    
    if (label === null) {
      // This route should not show breadcrumbs (login, etc)
      return [];
    }
    
    if (label === undefined) {
      if (isId) {
        // For IDs, we'll let the page component provide the actual name
        // Skip adding to breadcrumb here - it will be added by the page
        return;
      }
      // Capitalize and clean up segment
      label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    const isLast = index === segments.length - 1;
    
    const PARENT_HREF = { '/labs-v2': '/labs', '/ai-course': '/courses', '/external-courses': '/courses?tab=external', '/scorm': '/courses', '/quiz': '/dashboard' };
    breadcrumbs.push({
      label,
      href: isLast ? undefined : (PARENT_HREF[currentPath] || currentPath),
      isCurrent: isLast
    });
  });
  
  return breadcrumbs;
};

export function Breadcrumb({ 
  items = null, 
  className = '',
  showHome = true,
  pageTitle = null  // Optional: current page title to append
}) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  // Don't show breadcrumbs on auth pages or landing
  const hiddenPaths = ['/', '/login', '/register', '/forgot-password', '/enterprise-sso', '/sso/callback'];
  if (hiddenPaths.includes(location.pathname) || !isAuthenticated) {
    return null;
  }
  
  // Use provided items or auto-generate from path
  let breadcrumbItems = items || generateBreadcrumbs(location.pathname);
  
  // Append page title if provided and not already present
  if (pageTitle && breadcrumbItems.length > 0) {
    const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
    if (lastItem.label !== pageTitle) {
      // Make the last auto-generated item a link
      if (lastItem.isCurrent) {
        lastItem.href = location.pathname.split('/').slice(0, -1).join('/') || '/dashboard';
        lastItem.isCurrent = false;
      }
      breadcrumbItems.push({
        label: pageTitle,
        isCurrent: true
      });
    }
  }
  
  if (breadcrumbItems.length <= 1) {
    return null; // Don't show breadcrumb if only Dashboard
  }
  
  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`flex items-center text-sm text-zinc-400 ${className}`}
      data-testid="breadcrumb-nav"
    >
      <ol className="flex items-center flex-wrap gap-1">
        {breadcrumbItems.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 mx-1.5 text-zinc-600 flex-shrink-0" />
            )}
            
            {item.href && !item.isCurrent ? (
              <Link
                to={item.href}
                className="hover:text-white transition-colors flex items-center gap-1.5"
                data-testid={`breadcrumb-link-${index}`}
              >
                {item.isRoot && showHome && (
                  <Home className="h-3.5 w-3.5" />
                )}
                <span>{item.label}</span>
              </Link>
            ) : (
              <span 
                className={`flex items-center gap-1.5 ${item.isCurrent ? 'text-white font-medium' : ''}`}
                data-testid={`breadcrumb-current-${index}`}
              >
                {item.isRoot && showHome && (
                  <Home className="h-3.5 w-3.5" />
                )}
                <span className="truncate max-w-[200px]">{item.label}</span>
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Breadcrumb wrapper with consistent styling for page headers
 */
export function BreadcrumbHeader({ 
  items = null, 
  pageTitle = null,
  children 
}) {
  return (
    <div className="mb-6">
      <Breadcrumb items={items} pageTitle={pageTitle} className="mb-2" />
      {children}
    </div>
  );
}

/**
 * Hook to build breadcrumb items programmatically
 */
export function useBreadcrumb() {
  const location = useLocation();
  
  const buildItems = (customItems = []) => {
    const base = [{ label: 'Dashboard', href: '/dashboard', isRoot: true }];
    return [...base, ...customItems];
  };
  
  return { buildItems, currentPath: location.pathname };
}

export default Breadcrumb;
